import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import NotificationBell from "./NotificationBell";
import GlobalSearch from "./GlobalSearch";
import OfflineBanner from "./OfflineBanner";
import { Sidebar, MobileMenu, BrandMark, visibleLinksFor } from "./Navigation";

function LangToggle({ testid, withIcon }) {
  const { lang, setLang } = useLang();
  return (
    <button
      data-testid={testid}
      onClick={() => setLang(lang === "fr" ? "en" : "fr")}
      className={`flex items-center gap-2 text-xs uppercase tracking-widest text-slate-300 rounded-lg hover:bg-white/5 ${withIcon ? "px-3 py-1.5" : "px-2 py-1"}`}
    >
      {withIcon && <Languages className="w-4 h-4" />}
      {lang}
    </button>
  );
}

function MobileHeader({ open, onToggle }) {
  const { t } = useLang();
  return (
    <header className="lg:hidden sticky top-0 z-30 glass border-b border-white/5 px-4 py-3 flex items-center justify-between">
      <button data-testid="mobile-menu-btn" onClick={onToggle} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/5">
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
      <div className="flex items-center gap-2">
        <BrandMark size="w-7 h-7 rounded-md" />
        <div className="font-bold" style={{ fontFamily: "Outfit" }}>{t("app_name")}</div>
      </div>
      <div className="flex items-center gap-1">
        <NotificationBell variant="mobile" />
        <LangToggle testid="language-toggle-button-mobile" />
      </div>
    </header>
  );
}

function TopBar() {
  return (
    <div className="hidden lg:flex items-center justify-between gap-3 px-6 lg:px-8 py-4 border-b border-white/5 glass sticky top-0 z-10">
      <GlobalSearch />
      <div className="flex items-center gap-3">
        <NotificationBell />
        <LangToggle testid="language-toggle-button" withIcon />
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const links = visibleLinksFor(user);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="grain relative min-h-screen text-slate-100">
      <Sidebar user={user} links={links} onLogout={handleLogout} />
      <MobileHeader open={open} onToggle={() => setOpen(!open)} />
      {open && <MobileMenu links={links} onNavigate={() => setOpen(false)} onLogout={handleLogout} />}
      <main className="lg:pl-64">
        <TopBar />
        <OfflineBanner />
        <div className="lg:hidden px-4 pt-4"><GlobalSearch /></div>
        <div className="p-6 lg:p-8 relative z-[2]">{children}</div>
      </main>
    </div>
  );
}
